import { Injectable, inject, Injector } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, finalize, shareReplay } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { ApiService } from '@aeCore/services/api.service';

@Injectable({ providedIn: 'root' })
export class TokenService extends ApiService {
  private router = inject(Router);
  private jwtHelper = inject(JwtHelperService);

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TFA_COMPLETED_KEY = 'tfaCompleted';
  private refreshInFlight$: Observable<{
    accessToken: string;
    refreshToken: string;
  }> | null = null;

  constructor(protected override injector: Injector) {
    super(injector);
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  saveTokens(accessToken: string, refreshToken: string): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  setTfaCompleted(completed: boolean): void {
    sessionStorage.setItem(this.TFA_COMPLETED_KEY, JSON.stringify(completed));
  }

  isTfaCompleted(): boolean {
    return JSON.parse(sessionStorage.getItem(this.TFA_COMPLETED_KEY) || 'false');
  }

  removeTokens(): void {
    try {
      sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(this.TFA_COMPLETED_KEY);
      sessionStorage.removeItem('user');
    } catch {
      // ignore private browsing / quota errors
    }
  }

  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    return !token || this.jwtHelper.isTokenExpired(token);
  }

  refreshToken(): Observable<{ accessToken: string; refreshToken: string }> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.router.navigate(['/auth']);
      return throwError(() => new Error('No refresh token available'));
    }

    if (!this.refreshInFlight$) {
      const headers = new HttpHeaders({
        'refresh-token': 'true',
        Authorization: `Bearer ${refreshToken}`,
      });
      this.refreshInFlight$ = this.post<{
        accessToken: string;
        refreshToken: string;
      }>(['auth', 'refresh'], {}, { headers }).pipe(
        tap((tokens) => {
          this.saveTokens(tokens.accessToken, tokens.refreshToken);
        }),
        catchError((error) => {
          this.removeTokens();
          this.router.navigate(['auth/sign-in']);
          return throwError(() => error);
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
      );
    }

    return this.refreshInFlight$;
  }
}
